import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Building, Plus } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const KINDS = { boutique: "Boutique", bureau: "Bureau", atelier: "Atelier / production", entrepot: "Entrepôt", autre: "Autre" };
const EMPTY = { name: "", kind: "boutique", address: "", surface_m2: "" };

export const BizPremises = ({ company }) => {
  const qc = useQueryClient();
  const [v, setV] = useState(EMPTY);
  const list = company.premises || [];
  const canEdit = company.my_role === "director";
  const save = async (premises) => {
    try { await api.put(`/business/companies/${company.id}`, { premises }); qc.invalidateQueries(); return true; }
    catch (e) { toast.error(formatApiError(e)); return false; }
  };
  const add = async (e) => {
    e.preventDefault();
    if (await save([...list, { ...v, address: v.address || null, surface_m2: v.surface_m2 === "" ? null : Number(v.surface_m2) }])) { setV(EMPTY); toast.success("Local ajouté"); }
  };
  const total = list.reduce((s, p) => s + (p.surface_m2 || 0), 0);
  return (
    <section className="card-surface space-y-3 p-5" data-testid="biz-premises">
      <h2 className="flex items-center gap-2 font-bold"><Building className="h-4 w-4" /> Locaux ({list.length}){total > 0 && <span className="text-sm font-medium text-muted-foreground">· {total} m² au total</span>}</h2>
      {list.length ? <ul className="stagger grid gap-2 sm:grid-cols-2">{list.map((p, i) => (
        <li key={`${p.name}-${i}`} className="lift flex items-start gap-3 rounded-xl border border-border bg-background/70 p-3 text-sm" data-testid={`biz-premise-${i}`}>
          <div className="min-w-0 flex-1"><b>{p.name}</b><div className="text-xs text-muted-foreground">{KINDS[p.kind]}{p.surface_m2 ? ` · ${p.surface_m2} m²` : ""}</div>{p.address && <div className="text-xs">{p.address}</div>}</div>
          {canEdit && <ConfirmDelete title="Retirer ce local ?" text={p.name} testid={`biz-premise-delete-${i}`} onConfirm={() => save(list.filter((_, j) => j !== i))} />}
        </li>))}</ul> : <p className="text-sm text-muted-foreground">Aucun local renseigné.</p>}
      {canEdit && (
        <form onSubmit={add} className="grid gap-2 sm:grid-cols-[1fr_10rem_1fr_7rem_auto]" data-testid="biz-premise-form">
          <Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="Nom du local" required minLength={2} data-testid="biz-premise-name" />
          <Select value={v.kind} onValueChange={(k) => setV({ ...v, kind: k })}><SelectTrigger data-testid="biz-premise-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <Input value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} placeholder="Adresse" data-testid="biz-premise-address" />
          <Input type="number" min="0" value={v.surface_m2} onChange={(e) => setV({ ...v, surface_m2: e.target.value })} placeholder="m²" data-testid="biz-premise-surface" />
          <Button type="submit" className="rounded-full" data-testid="biz-premise-add"><Plus className="mr-1 h-4 w-4" /> Ajouter</Button>
        </form>
      )}
    </section>
  );
};
